import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Page from "components/page/Page";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faMinusCircle } from "@fortawesome/pro-regular-svg-icons";
import DataTable, { HeaderType } from "components/dataTable/DataTable";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import {
  GetWebsiteBuilderFormSubmissions,
  GetWebsiteBuilderFormSummary,
} from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import { IWebsiteBuilderFormSubmission, IWebsiteBuilderFormSummary } from "redux/reducers/models/websiteBuilder";
import { useWebsite } from "../shared/hooks/useWebsite";
import { useWebsiteBuilderLoadingState } from "../shared/hooks/useWebsiteBuilderLoadingState";
import { WebsiteBuilderLoadingContent } from "./components/loadingContent/WebsiteBuilderLoadingContent";
import { capitalize, cloneDeep, flatten, union } from "lodash";
import "./websiteBuilderFormSubmissions.scss";
import Portal from "elements/Portal";
import { ButtonNew as Button } from "components/buttonNew";
import classNames from "classnames";
import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import { isPositiveInteger } from "helpers/Helpers";

interface IWebsiteBuilderFormSubmissionsParams {
  formId: string;
}

const FORM_SUBMISSIONS_LIMIT = 25;

function getReadableSubmissionKey(key: string) {
  const customPrefix = "custom_";
  const filteredKey = key.startsWith(customPrefix) ? key.slice(customPrefix.length) : key;

  return filteredKey.split("_").map((v, i) => i === 0 ? capitalize(v) : v).join(" ");
}

export function WebsiteBuilderFormSubmissions() {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { website, websiteLoadingStatus } = useWebsite({ websiteMustExist: true });

  const { formId: formIdParam } = useParams<IWebsiteBuilderFormSubmissionsParams>();

  const formId = Number(formIdParam);
  const formIdIsValid = isPositiveInteger(formIdParam);

  const {
    state: formSummary,
    stateExists: formSummaryExists,
    stateLoadingStatus: formSummaryLoadingStatus,
    loadInProgress: formSummaryLoadInProgress,
    loadFailed: formSummaryLoadFailed,
    loadSucceeded: formSummaryLoadSucceeded,
  } = useWebsiteBuilderLoadingState<IWebsiteBuilderFormSummary>({
    state: null,
    stateExists: false,
    stateLoadingStatus: "loading",
  });

  const {
    state: initialFormSubmissions,
    stateExists: initialFormSubmissionsExist,
    stateLoadingStatus: initialFormSubmissionsLoadingStatus,
    loadInProgress: initialFormSubmissionsLoadInProgress,
    loadFailed: initialFormSubmissionsLoadFailed,
    loadSucceeded: initialFormSubmissionsLoadSucceeded,
  } = useWebsiteBuilderLoadingState<IWebsiteBuilderFormSubmission[]>({
    state: [],
    stateExists: false,
    stateLoadingStatus: "loading",
  });

  const loadFormData = useCallback(
    async (token: CancelToken) => {
      if (website?.id == null) {
        return;
      }

      if (!formIdIsValid) {
        formSummaryLoadFailed();
        initialFormSubmissionsLoadFailed();
        dispatch(showError(t("Invalid form selected")));
        return;
      }

      formSummaryLoadInProgress();
      initialFormSubmissionsLoadInProgress();

      const [formSummaryRes, formSubmissionsRes] = await Promise.all([
        GetWebsiteBuilderFormSummary(
          {
            website_id: website.id,
            form_id: formId,
          },
          false,
          token,
        ),
        GetWebsiteBuilderFormSubmissions(
          {
            form_id: formId,
            offset: 0,
            limit: FORM_SUBMISSIONS_LIMIT,
          },
          false,
          token,
        ),
      ]);

      if (formSummaryRes.status !== StatusCode.OK) {
        formSummaryLoadFailed();

        if (!token.reason) {
          dispatch(showError(typeof formSummaryRes.data === "string" ? formSummaryRes.data : t("Error loading form")));
        }
      } else {
        const formSummary = formSummaryRes.data?.[0];

        formSummaryLoadSucceeded(formSummary, formSummary != null);
      }

      if (formSubmissionsRes.status !== StatusCode.OK) {
        initialFormSubmissionsLoadFailed();

        if (!token.reason) {
          dispatch(
            showError(
              typeof formSubmissionsRes.data === "string" ? formSubmissionsRes.data : t("Error loading submissions"),
            ),
          );
        }
      } else {
        initialFormSubmissionsLoadSucceeded(formSubmissionsRes.data ?? [], true);
      }
    },
    [website?.id, formIdIsValid, formId],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadFormData(source.token);

    return () => source.cancel();
  }, [loadFormData]);

  function renderContent() {
    if (
      websiteLoadingStatus !== "succeeded" ||
      formSummaryLoadingStatus !== "succeeded" ||
      initialFormSubmissionsLoadingStatus !== "succeeded" ||
      !formSummaryExists ||
      !initialFormSubmissionsExist
    ) {
      return null;
    }

    if (initialFormSubmissions.length === 0) {
      return (
        <div className="form-submissions-no-data-card">
          <div className="no-data">
            <div className="no-data-icon">
              <FontAwesomeIcon icon={faMinusCircle} />
            </div>
            <div className="no-data-message">
              <div className="no-data-message-primary">{t("No submissions found")}</div>
              <div className="no-data-message-secondary">{t("This form has not received any submissions yet.")}</div>
            </div>
          </div>
        </div>
      );
    } else {
      return <FormSubmissionsTable formId={formId} initialFormSubmissions={initialFormSubmissions} />;
    }
  }

  return (
    <Page
      className="website-builder-form-submissions-page"
      narrow
      title={formSummary?.name ?? ""}
      subtitle={formSummary?.name ? t("View the form submissions. Fields may vary if the form has been edited.") : ""}
      breadcrumbs={[
        { label: t("Forms & Submissions"), url: "/admin/website-builder-forms" },
        { label: formSummary?.name ?? "" },
      ]}
    >
      <WebsiteBuilderLoadingContent
        loading={
          websiteLoadingStatus === "standby" ||
          websiteLoadingStatus === "loading" ||
          formSummaryLoadingStatus === "loading" ||
          initialFormSubmissionsLoadingStatus === "loading"
        }
      >
        {renderContent()}
      </WebsiteBuilderLoadingContent>
    </Page>
  );
}

interface IFormSubmissionsTableProps {
  formId: number;
  initialFormSubmissions: IWebsiteBuilderFormSubmission[];
}

function FormSubmissionsTable(props: IFormSubmissionsTableProps) {
  const { formId, initialFormSubmissions } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const paginationTriggered = useRef<boolean>(false);

  const [selectedFormSubmissionId, setSelectedFormSubmissionId] = useState<number>(null);
  const [formSubmissions, setFormSubmissions] = useState<IWebsiteBuilderFormSubmission[]>(cloneDeep(initialFormSubmissions));
  const [formSubmissionsLoading, setFormSubmissionsLoading] = useState<boolean>(false);
  const [formSubmissionsOffset, setFormSubmissionsOffset] = useState<number>(0);

  useEffect(() => {
    paginationTriggered.current = false;

    setSelectedFormSubmissionId(null);
    setFormSubmissions(cloneDeep(initialFormSubmissions));
    setFormSubmissionsLoading(false);
    setFormSubmissionsOffset(0);
  }, [initialFormSubmissions]);

  const loadFormSubmissions = useCallback(
    async (token: CancelToken) => {
      setFormSubmissionsLoading(true);

      const formSubmissionsRes = await GetWebsiteBuilderFormSubmissions(
        {
          form_id: formId,
          offset: formSubmissionsOffset,
          limit: FORM_SUBMISSIONS_LIMIT,
        },
        false,
        token,
      );

      setFormSubmissionsLoading(false);

      if (formSubmissionsRes.status !== StatusCode.OK) {
        if (!token.reason) {
          dispatch(
            showError(
              typeof formSubmissionsRes.data === "string" ? formSubmissionsRes.data : t("Error loading submissions"),
            ),
          );
        }

        setFormSubmissions([]);
      } else {
        setFormSubmissions(formSubmissionsRes.data ?? []);
      }
    },
    [formId, formSubmissionsOffset],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (paginationTriggered.current) {
      void loadFormSubmissions(source.token);
    }

    return () => source.cancel();
  }, [loadFormSubmissions]);

  const submissionKeys = useMemo(() => {
    return union(flatten(formSubmissions.map(formSubmission => Object.keys(formSubmission.submission)))).sort();
  }, [formSubmissions]);

  const columns: HeaderType<undefined>[] = useMemo(() => {
    return [
      { label: t("Submission time") },
      ...submissionKeys.map(key => ({ label: getReadableSubmissionKey(key) })),
    ];
  }, [submissionKeys]);

  return (
    <>
      <div className="form-submissions-table-container">
        <DataTable
          className="form-submissions-table"
          columns={columns}
          loading={formSubmissionsLoading}
          footer={{
            tableLimit: FORM_SUBMISSIONS_LIMIT,
            tableOffset: formSubmissionsOffset,
            handleTableOffset: direction => {
              paginationTriggered.current = true;
              setSelectedFormSubmissionId(null);

              setFormSubmissionsOffset(prev =>
                direction === "prev"
                  ? Math.max(0, prev - FORM_SUBMISSIONS_LIMIT)
                  : prev + FORM_SUBMISSIONS_LIMIT,
              );
            },
            disableNextOffset: formSubmissions.length < FORM_SUBMISSIONS_LIMIT,
          }}
        >
          {formSubmissions.map(formSubmission => {
            return (
              <tr
                key={formSubmission.id}
                className={classNames("form-submissions-table-submission-row", {
                  "submission-row-selected": formSubmission.id === selectedFormSubmissionId,
                })}
                onClick={() => setSelectedFormSubmissionId(prev => prev === formSubmission.id ? null : formSubmission.id)}
              >
                <td>{formSubmission.created_at != null ? moment(formSubmission.created_at).format("MMM D, YYYY h:mm A") : ""}</td>
                {submissionKeys.map(key => {
                  return (
                    <td key={key}>
                      {formSubmission.submission[key] ?? ""}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </DataTable>
      </div>

      <FormSubmissionDetails
        formSubmissions={formSubmissions}
        selectedFormSubmissionId={selectedFormSubmissionId}
        setSelectedFormSubmissionId={setSelectedFormSubmissionId}
      />
    </>
  );
}

interface IFormSubmissionDetailsProps {
  formSubmissions: IWebsiteBuilderFormSubmission[];
  selectedFormSubmissionId: number;
  setSelectedFormSubmissionId: React.Dispatch<React.SetStateAction<number>>;
}

function FormSubmissionDetails(props: IFormSubmissionDetailsProps) {
  const { formSubmissions, selectedFormSubmissionId, setSelectedFormSubmissionId } = props;

  const { t } = useTranslation();

  const detailsRef = useOnclickOutside(() => {
    setSelectedFormSubmissionId(null);
  }, { ignoreClass: "form-submissions-table-submission-row"});

  const selectedFormSubmission = useMemo(() => {
    if (selectedFormSubmissionId == null) {
      return null;
    } else {
      return formSubmissions.find(formSubmission => formSubmission.id === selectedFormSubmissionId);
    }
  }, [selectedFormSubmissionId, formSubmissions]);

  const selectedSubmissionKeys: string[] = useMemo(() => {
    if (selectedFormSubmission?.submission == null) {
      return null;
    }

    return Object.keys(selectedFormSubmission.submission).sort();
  }, [selectedFormSubmission?.submission]);

  const detailsActive = selectedFormSubmission != null;

  return (
    <Portal isMounted={true}>
      <div
        ref={detailsRef}
        className={classNames("website-builder-form-submission-details", {
          "details-active": detailsActive,
        })}
      >
        {detailsActive && (
          <>
            <div className="details-header">
              <div className="details-header-primary">
                <h2>{t("Submission details")}</h2>
                <p>{selectedFormSubmission.created_at != null ? moment(selectedFormSubmission.created_at).format("MMM D, YYYY h:mm A") : ""}</p>
              </div>
              <FontAwesomeIcon
                className="details-header-close"
                icon={faClose}
                onClick={() => setSelectedFormSubmissionId(null)}
              />
            </div>
            <div className="details-content">
              <dl>
                {selectedSubmissionKeys.map(key => {
                  return (
                    <Fragment key={key}>
                      <dt>{getReadableSubmissionKey(key)}</dt>
                      <dd>{selectedFormSubmission.submission[key] ?? ""}</dd>
                    </Fragment>
                  );
                })}
              </dl>
            </div>
            <div className="details-footer">
              <Button
                type="secondary"
                onClick={() => setSelectedFormSubmissionId(null)}
              >
                {t("Cancel")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Portal>
  );
}